import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import { DOCUMENT_CATEGORIES } from "../common/constants";
import { updateDocument } from "../services/documentStorageService";
import { formatDateTime } from "../../../utils/formatters/date";

/**
 * Formato legible para tamaño de archivo.
 */
const formatSize = (bytes) => {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Dialog para ver y editar detalles de un documento.
 *
 * @param {{ open: boolean, doc: Object|null, onClose: () => void, onSaved: () => void }} props
 */
export default function DocumentDetailDialog({ open, doc, onClose, onSaved }) {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (doc) {
      setCategory(doc.category || "Other");
      setDescription(doc.description || "");
    }
  }, [doc]);

  const handleSave = () => {
    if (!doc) return;
    updateDocument(doc.id, { category, description });
    onSaved();
    onClose();
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Document Details</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Metadata de solo lectura */}
          <DetailRow label="File Name" value={doc.fileName} />
          <DetailRow label="Type" value={doc.fileType} />
          <DetailRow label="Size" value={formatSize(doc.fileSize)} />
          <DetailRow label="Uploaded By" value={doc.uploadedByName} />
          <DetailRow
            label="Date"
            value={formatDateTime(doc.uploadedAt)}
          />

          <Divider />

          {/* Campos editables */}
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            size="small"
            fullWidth
          >
            {DOCUMENT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 100 }}>
        {label}:
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}
